import i18n from "@/i18n";
import {
  isEmail,
  isInt,
  isMobilePhone,
  isStrongPassword,
  isURL,
  matches,
} from "validator";

export class Validators {
  static rules = {
    required: (value) => this.requiredValidation(value),
    email: (value) => this.emailValidation(value),
    reaNumber: (value) => this.reaNumberValidation(value),
    enterpriseRegistrationNumber: (value) =>
      this.registrationNumberValidation(value),
    passwordValidation: (value) => this.passwordValidation(value),
    webSiteUrlValidator: (value) => this.webSiteUrlValidator(value),
    internationalMobileNumber: (value) => this.internationalMobileNumber(value),
    samePasswordValidation: (value, otherPassword) =>
      this.samePasswordValidation(value, otherPassword),
    integerPositiveNumber: (value) => this.integerPositiveNumber(value),
    positiveNumber: (value) => this.positiveNumberValidation(value),
    percentage: (value) => this.percentageValidation(value),
    minValueNegativeOne: (value) => this.minValueNegativeOne(value),
    imageFileType: (file) => this.imageFileTypeValidation(file),
    imageFileSize: (file) => this.imageFileSizeValidation(file),
  };

  static imageFileTypeValidation(file) {
    if (!file || typeof file !== "object" || !file.type) return true;
    const imageTypes = ["image/png", "image/jpeg", "image/webp"];
    return (
      imageTypes.includes(file.type) || i18n.t("validator-image-file-type")
    );
  }

  static imageFileSizeValidation(file, maxKb = 200) {
    if (!file || typeof file !== "object" || !file.size) return true;
    return (
      file.size <= maxKb * 1024 ||
      i18n.t("validator-image-file-size", { maxKb })
    );
  }

  static requiredValidation(inputToValidate) {
    return (
      (inputToValidate !== null &&
        inputToValidate !== undefined &&
        inputToValidate !== "") ||
      i18n.t("validator-required")
    );
  }

  static passwordValidation(inputToValidate) {
    if (inputToValidate === undefined || inputToValidate.length === 0)
      return true;
    return (
      isStrongPassword(inputToValidate, {
        minLength: 8,
        minLowercase: 0,
        minUppercase: 0,
        minNumbers: 1,
        minSymbols: 1,
        returnScore: false,
      }) || i18n.t("validator-password")
    );
  }

  static samePasswordValidation(inputToValidate, otherPassword) {
    return (
      inputToValidate === otherPassword || i18n.t("validator-password-confirm")
    );
  }

  static internationalMobileNumber(inputToValidate) {
    if (inputToValidate === undefined || inputToValidate.length === 0)
      return true;
    return (
      isMobilePhone(inputToValidate, "any", { strictMode: true }) ||
      i18n.t("validator-international-phone")
    );
  }

  static reaNumberValidation(inputToValidate) {
    if (inputToValidate === undefined || inputToValidate.length === 0)
      return true;

    const reaRegex = /^[A-Z]{2}\d{7}$|^\d{7}$/i;

    return matches(inputToValidate, reaRegex) || i18n.t("validator-rea-number");
  }

  static registrationNumberValidation(inputToValidate) {
    if (inputToValidate === undefined || inputToValidate.length === 0)
      return true;

    const registrationNumberRegex = /^\d{11}$/;

    return (
      matches(inputToValidate, registrationNumberRegex) ||
      i18n.t("validator-registration-number") // Messaggio di errore personalizzato
    );
  }

  static webSiteUrlValidator(inputToValidate) {
    if (inputToValidate === undefined || inputToValidate.length === 0)
      return true;
    return isURL(inputToValidate, {}) || i18n.t("validator-url");
  }

  static emailValidation(inputToValidate) {
    if (inputToValidate === undefined || inputToValidate.length === 0)
      return true;
    return (
      isEmail(inputToValidate, { allow_underscores: true }) ||
      i18n.t("validator-email")
    );
  }

  static integerPositiveNumber(inputToValidate) {
    if (inputToValidate === undefined || inputToValidate.length === 0)
      return true;

    if (typeof inputToValidate === "number") {
      inputToValidate = inputToValidate.toString();
    }
    return (
      isInt(inputToValidate, { min: 0 }) || i18n.t("validator-integer-positive")
    );
  }

  static positiveNumberValidation(inputToValidate) {
    if (
      inputToValidate === undefined ||
      inputToValidate === null ||
      inputToValidate === ""
    )
      return true;

    const number = Number(inputToValidate);
    return (!isNaN(number) && number >= 0) || i18n.t("value-must-be-positive");
  }

  static percentageValidation(inputToValidate) {
    if (
      inputToValidate === undefined ||
      inputToValidate === null ||
      inputToValidate === ""
    )
      return true;

    const number = Number(inputToValidate);
    return (
      (!isNaN(number) && number >= 0 && number <= 100) ||
      i18n.t("value-must-be-between-0-100")
    );
  }

  static minValueNegativeOne(inputToValidate) {
    if (
      inputToValidate === undefined ||
      inputToValidate === null ||
      inputToValidate === ""
    )
      return true;

    const number = Number(inputToValidate);
    return number >= -1 || i18n.t("validator-min-value-negative-one");
  }
}

/* 
ISTRUZIONI PER L'UTILIZZO DELLE RULES IN OGNI VISTA

1) Importare i validators
import { Validators } from "../../utility/validators.js";

2) Inserire le rules nei data del componente
rules: { ...Validators.rules },

3) Inserire nell'html un v-form che racchiuda tutti i campi con delle rules ed assegnargli un ref (in questo caso form)
<v-form ref="form">
.....
</v-form>

4) Assegnare le specifiche rules come attributes di un determinato campo di input all'interno di un array
<v-text-field :rules="[ rules.required, rules.integerNumber ]"

5) Creare un metodo che validi il form 
validateFields: function() {
    return this.$refs.form.validate();
},

6) Porre il metodo di validazione precedentemente creato come condizione per il salvataggio dei dati
saveData: async function() {
    if(this.validateFields) {
        do something ...
    }
}

7) Eventualmente creare una computed ed un watcher per cambiare la virgola dei numeri decimali con un punto
computed: {
    inputDecimalList: function() {
        let dataModelsList = [
            "objectModel1.key1",
            "objectModel1.key2",
            "model2"
        ]
        let object = {
            values: `${this.objectModel1.key1}|${this.objectModel1.key2}|${this.model2}`,
            models: [...dataModelsList]
        }
        return object
    },
},
watch: {
    inputDecimalList: function(value) {
        const vm = this;
        Formatters.formatAllDecimalNumberWithDot(value, vm)
    }
},
*/

/* 
ISTRUZIONI PER L'AGGIUNTA DI UNA RULE ALL'INTERNO DELL'OGGETTO RULES

1) Creare una funzione che esegue la validazione
static requiredValidation(inputToValidate) {
    return (inputToValidate !== null && inputToValidate !== undefined && inputToValidate !== "") || "Required";
}

2) Assegnarla come ad una chiave dell'oggetto rules tramite arrow function
static rules = {
    required: (value) => this.requiredValidation(value),
    ...
}
*/
